import { API_BASE, SECURE_API_BASE } from './config';

export interface CreateAccessRequestRequest {
  email: string;
  message?: string;
}

export interface CreateFeedbackRequest {
  email: string;
  feedback_type: 'general_feedback' | 'bug_report' | 'feature_request';
  message?: string;
}

export interface FeedbackItem {
  feedback_id: string;
  email: string;
  message_type: 'request_access' | 'feedback';
  moderation: 'access_requests' | 'general_feedback';
  feedback_type: 'invite_request' | 'general_feedback' | 'bug_report' | 'feature_request';
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  created_at: string;
  message?: string;
}

// Public API - Create access request
export const createAccessRequest = async (request: CreateAccessRequestRequest): Promise<void> => {
  const response = await fetch(`${SECURE_API_BASE}/request-access`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to create access request');
  }
};

// Secure API - Create feedback
export const createFeedback = async (feedback: CreateFeedbackRequest): Promise<void> => {
  const response = await fetch(`${SECURE_API_BASE}/feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
    body: JSON.stringify(feedback),
  });

  if (!response.ok) {
    throw new Error('Failed to create feedback');
  }
};

// Secure API - Get access requests (moderators)
export const getAccessRequests = async (): Promise<FeedbackItem[]> => {
  const response = await fetch(`${SECURE_API_BASE}/access-requests`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch access requests');
  }

  return response.json();
};

// Secure API - Get feedback (moderators)
export const getFeedback = async (): Promise<FeedbackItem[]> => {
  const response = await fetch(`${SECURE_API_BASE}/feedback`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch feedback');
  }

  return response.json();
};

// Secure API - Send invite email
export const sendInviteEmail = async (requestId: string): Promise<void> => {
  const response = await fetch(`${SECURE_API_BASE}/access-requests/${requestId}/send-invite`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to send invite email');
  }
};

// Secure API - Resolve feedback/access request
export const resolveFeedback = async (feedbackId: string, resolution: string, notes?: string): Promise<void> => {
  const endpoint = resolution === 'resolved' && notes === 'invite_sent' 
    ? `${SECURE_API_BASE}/access-requests/${feedbackId}/resolve`
    : `${SECURE_API_BASE}/feedback/${feedbackId}/resolve`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
    body: JSON.stringify({ resolution, notes }),
  });

  if (!response.ok) {
    throw new Error('Failed to resolve feedback');
  }
};